import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export const AuditType = {
  create: 'create',
  update: 'update',
} as const;
export type AuditType = (typeof AuditType)[keyof typeof AuditType];

export const AuditDomain = {
  user: 'user',
  profile: 'profile',
  account: 'account',
  accountAddress: 'accountAddress',
  accountContact: 'accountContact',
  accountAttachments: 'accountAttachments',
  priceTable: 'priceTable',
  externalLink: 'externalLink',
  creditAnalysis: 'creditAnalysis',
  accountDocuments: 'accountDocuments',
} as const;
export type AuditDomain = (typeof AuditDomain)[keyof typeof AuditDomain];

export interface AuditMetadata {
  field: string;
  after: unknown;
  before: unknown;
}

export interface AuditUser {
  id: string;
  name: string;
  email: string;
}

export interface Audit {
  id: string;
  affected_entity_id: string;
  domain: AuditDomain;
  type: AuditType;
  action: string;
  metadata: AuditMetadata[];
  created_at: string;
  user: AuditUser;
}

export interface PaginatedAuditResponse {
  docs: Audit[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface AuditQueryParams {
  page?: number;
  limit?: number;
  domain?: AuditDomain;
  affected_entity_id?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface AuditByControlCodeParams {
  page?: number;
  limit?: number;
}

export const auditApi = {
  findMany: async (params?: AuditQueryParams): Promise<PaginatedAuditResponse> => {
    const { data } = await api.get<ApiPaginatedResponse<Audit>>('/admin/audit', { params });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<Audit> => {
    const { data } = await api.get<ApiResponse<Audit>>(`/admin/audit/${id}`);
    return data.data;
  },

  findByEntity: async (
    entityId: string,
    params?: Omit<AuditQueryParams, 'affected_entity_id'>,
  ): Promise<PaginatedAuditResponse> => {
    const { data } = await api.get<ApiPaginatedResponse<Audit>>(`/admin/audit/entity/${entityId}`, {
      params,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  // Find audit logs for profile permission changes related to a specific control code
  findByControlCode: async (
    controlCode: string,
    params?: AuditByControlCodeParams,
  ): Promise<PaginatedAuditResponse> => {
    const { data } = await api.get<ApiPaginatedResponse<Audit>>('/admin/audit/by-control-code', {
      params: { control_code: controlCode, ...params },
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },
};
